(() => {
  const TOKEN = "b3X9zP7QmW4kN8rH2sF1yT6cV0dL5aE";
  const API_BASE = "https://variants-shared-inventory.addify-app.com/api/fetch-inventory-data";

  // Cache key already includes the cart signature, so cart changes always refetch.
  // The TTL only covers stock changing from other shoppers' orders.
  const CACHE_TTL_MS = 30_000;
  const PREFETCH_CONCURRENCY = 3;
  const PREFETCH_MAX_VARIANTS = 40;
  const PERSIST_KEY = "sharedInventory:cache:v1";
  const PERSIST_MAX_KEYS = 200;

  // Body classes set from the PDP API response
  const BODY_CLASS_ACTIVE = "shared-inventory-active";   // status: true
  const BODY_CLASS_SOLDOUT = "shared-inventory-soldout"; // sold out (Addify group stock <= 0 OR Shopify variant unavailable)

  // Buttons to hide while the selected variant is sold out (Buy it now / Shop Pay).
  // Add your theme's selectors here if needed.
  const SHOP_BUTTON_SELECTORS = [".shopify-payment-button", "shop-pay-wallet-button"];

  // Klaviyo back-in-stock button: always hidden on shared-variant products,
  // untouched on normal products. Add your theme's selector here if needed.
  const KLAVIYO_BIS_SELECTORS = [".klaviyo-bis-trigger"];

  const state = {
    cache: new Map(), // key -> {status,data,ts}
    currentVariantId: null,
    currentProductId: null,
    cartRecheckTimers: [],
    inflightApi: new Map(), // key -> Promise
    inflightCart: null, // Promise
    cartSnapshot: null,
    cartSnapshotAt: 0,
    cartApplyInProgress: false,
    cartApplyQueued: false,
    lastCartSigPurged: null,
    lastAllowedQty: null,
    lastAllowedVariantId: null,
    prefetchedFor: null,
    nativeSoldOut: new Map(), // variantId -> true when Shopify itself marks the variant unavailable
    lastApi: null
  };

  // Inject the hide rule once, so buttons Shopify renders later are hidden too.
  (function injectShopButtonCss() {
    try {
      if (document.getElementById("shared-inventory-css")) return;
      const style = document.createElement("style");
      style.id = "shared-inventory-css";
      const shopRule = SHOP_BUTTON_SELECTORS
        .map((sel) => `body.${BODY_CLASS_SOLDOUT} ${sel}`)
        .join(",\n") + " { display: none !important; }";
      const klaviyoRule = KLAVIYO_BIS_SELECTORS
        .map((sel) => `body.${BODY_CLASS_ACTIVE} ${sel}`)
        .join(",\n") + " { display: none !important; }";
      style.textContent = shopRule + "\n" + klaviyoRule;
      (document.head || document.documentElement).appendChild(style);
    } catch {
    }
  })();

  function safeJsonParse(text, fallback) {
    try {
      return JSON.parse(text);
    } catch {
      return fallback;
    }
  }

  function loadPersistentCache() {
    try {
      const raw = sessionStorage.getItem(PERSIST_KEY);
      const obj = raw ? safeJsonParse(raw, {}) : {};
      return obj && typeof obj === "object" ? obj : {};
    } catch {
      return {};
    }
  }

  function savePersistentCache(obj) {
    try {
      sessionStorage.setItem(PERSIST_KEY, JSON.stringify(obj));
    } catch {
    }
  }

  function prunePersistentCache(obj) {
    try {
      const entries = Object.entries(obj);
      if (entries.length <= PERSIST_MAX_KEYS) return obj;
      entries.sort((a, b) => (b[1]?.ts || 0) - (a[1]?.ts || 0));
      const keep = entries.slice(0, PERSIST_MAX_KEYS);
      return Object.fromEntries(keep);
    } catch {
      return obj;
    }
  }

  function purgePersistentCacheForCart(cartArray) {
    // Keep only entries matching the current cart signature.
    // This automatically removes old keys when cart items are removed.
    try {
      const sig = cartSignature(cartArray || []);
      if (sig && state.lastCartSigPurged === sig) return;
      state.lastCartSigPurged = sig;
      const obj = loadPersistentCache();
      const filtered = {};
      for (const [k, v] of Object.entries(obj)) {
        if (typeof k === "string" && k.endsWith(`:${sig}`)) filtered[k] = v;
      }
      savePersistentCache(prunePersistentCache(filtered));
    } catch {
    }
  }

  function getShopName() {
    return window?.Shopify?.shop || "";
  }

  async function fetchCart() {
    try {
      const now = Date.now();
      if (state.cartSnapshot && now - state.cartSnapshotAt < 750) return state.cartSnapshot;
      if (state.inflightCart) return await state.inflightCart;

      state.inflightCart = (async () => {
        const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, { credentials: "same-origin" });
        if (!res.ok) return null;
        const json = await res.json();
        state.cartSnapshot = json;
        state.cartSnapshotAt = Date.now();
        return json;
      })();

      return await state.inflightCart;
    } catch {
      return null;
    } finally {
      state.inflightCart = null;
    }
  }

  // API expects: [{ [variant_id]: quantity }, ...]
  function buildCartArray(cart) {
    try {
      if (!cart?.items) return [];
      return cart.items.map((item) => ({ [item.variant_id]: item.quantity }));
    } catch {
      return [];
    }
  }

  function cartSignature(cartArray) {
    try {
      return cartArray
        .flatMap((obj) => Object.keys(obj).map((k) => `${k}:${obj[k]}`))
        .sort()
        .join("|");
    } catch {
      return "";
    }
  }

  async function fetchSharedInventory({ productId, variantId, cartArray }) {
    const shopName = getShopName();
    if (!shopName || !productId || !variantId) return { status: false };

    const sig = cartSignature(cartArray || []);
    const key = `${productId}:${variantId}:${sig}`;
    const cached = state.cache.get(key);
    const now = Date.now();
    if (cached && now - cached.ts < CACHE_TTL_MS) return cached;

    if (state.inflightApi.has(key)) return await state.inflightApi.get(key);

    // sessionStorage cache
    try {
      const persisted = loadPersistentCache();
      const hit = persisted[key];
      if (hit && now - (hit.ts || 0) < CACHE_TTL_MS) {
        state.cache.set(key, hit);
        return hit;
      }
    } catch {
    }

    const cartParam = `&cartArray=${encodeURIComponent(JSON.stringify(cartArray || []))}`;
    const url = `${API_BASE}?shopName=${shopName}&productId=${productId}&variantId=${variantId}${cartParam}`;

    const promise = (async () => {
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${TOKEN}` }
        });
        const json = await res.json();
        const normalized = { ...(json || {}), ts: now };
        // console.log(normalized,'thanksss')
        state.cache.set(key, normalized);
        try {
          const persisted = loadPersistentCache();
          persisted[key] = normalized;
          savePersistentCache(prunePersistentCache(persisted));
        } catch {
        }
        return normalized;
      } catch {
        const fallback = { status: false, ts: now };
        state.cache.set(key, fallback);
        try {
          const persisted = loadPersistentCache();
          persisted[key] = fallback;
          savePersistentCache(prunePersistentCache(persisted));
        } catch {
        }
        return fallback;
      } finally {
        state.inflightApi.delete(key);
      }
    })();

    state.inflightApi.set(key, promise);
    return await promise;
  }

  function clampNumber(val, min, max) {
    const n = Number(val);
    if (Number.isNaN(n)) return min;
    return Math.max(min, Math.min(max, n));
  }

  function setInputMax(input, max) {
    if (!input) return;
    if (max == null || !Number.isFinite(max)) {
      // Only clear a max this script set; leave the theme's own limits alone
      if (input.dataset.sharedInvMax === "1") {
        input.removeAttribute("max");
        delete input.dataset.sharedInvMax;
      }
      return;
    }
    input.setAttribute("max", String(max));
    input.dataset.sharedInvMax = "1";
  }

  function ensureValueNotAboveMax(input, { emitChange } = { emitChange: true }) {
    if (!input) return;
    const min = Number(input.getAttribute("min") || 1);
    const maxAttr = input.getAttribute("max");
    if (!maxAttr) return;
    const max = Number(maxAttr);
    if (!Number.isFinite(max)) return;

    const clamped = clampNumber(input.value, min, max);
    if (String(clamped) !== String(input.value)) {
      input.value = String(clamped);
      if (emitChange) input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  function getPdpQtyInput() {
    return document.querySelector('input[name="quantity"][data-variant-id][data-product-id]');
  }

  function getCartQtyInputs() {
    return Array.from(document.querySelectorAll('input[data-line-key][data-variant-id][data-product-id]'));
  }

  function getCartQtyInputByLineKey(lineKey) {
    if (!lineKey) return null;
    try {
      return document.querySelector(`input[data-line-key="${CSS.escape(lineKey)}"][data-variant-id][data-product-id]`);
    } catch {
      return document.querySelector(`input[data-line-key="${lineKey}"][data-variant-id][data-product-id]`);
    }
  }

  function getAtcButtonsForVariant(variantId) {
    if (!variantId) return [];
    const buttons = Array.from(document.querySelectorAll('button[name="add"]'));

    return buttons.filter((btn) => {
      const formId = btn.getAttribute("form");
      const form = formId ? document.getElementById(formId) : btn.closest("form");
      if (!form) return false;
      const idInput = form.querySelector('input[name="id"]');
      return idInput && String(idInput.value) === String(variantId);
    });
  }

  function setButtonSoldOutState(button, isSoldOut) {
    if (!button) return;
    if (!button.dataset.originalText) {
      button.dataset.originalText = (button.textContent || "").trim();
    }

    if (isSoldOut) {
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.textContent = "Sold out";
      button.dataset.sharedInvSoldOut = "1";
    } else {
      // Only undo what this script did; never re-enable a button the theme disabled
      if (button.dataset.sharedInvSoldOut !== "1") return;
      button.disabled = false;
      button.removeAttribute("aria-disabled");
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
      }
      delete button.dataset.sharedInvSoldOut;
    }
  }

  function setAtcStateForVariant(variantId, isSoldOut) {
    getAtcButtonsForVariant(variantId).forEach((btn) => setButtonSoldOutState(btn, isSoldOut));
  }

  // Reflect the PDP API response on <body>.
  // Toggled (not just added) so the classes clear when the shopper switches variants.
  function isNativeSoldOut(variantId) {
    return state.nativeSoldOut.get(String(variantId)) === true;
  }

  function isApiSoldOut(api) {
    if (!api?.status) return false;
    const remaining = Number(api?.data?.group_total_quantity);
    return Number.isFinite(remaining) && remaining <= 0;
  }

  // Classes are only set for shared-variant products (Addify status: true).
  // Normal products get no classes, so nothing on them is hidden.
  function setBodyInventoryClass(api, variantId) {
    if (!document.body) return;
    const shared = !!api?.status;
    document.body.classList.toggle(BODY_CLASS_ACTIVE, shared);
    document.body.classList.toggle(
      BODY_CLASS_SOLDOUT,
      shared && (isNativeSoldOut(variantId) || isApiSoldOut(api))
    );
  }

  // Read Shopify's own availability for the selected variant on page load,
  // from the theme-rendered add-to-cart button, before this script touches it.
  function captureNativeStateOnLoad(variantId) {
    try {
      const btn = getAtcButtonsForVariant(variantId)[0];
      if (btn) state.nativeSoldOut.set(String(variantId), btn.disabled === true);
    } catch {
    }
  }

  function updateCartUiForInput(input) {
    // Disable the "+" control visually/behaviorally when user hits max (shared inventory reached).
    try {
      const maxAttr = input.getAttribute("max");
      if (!maxAttr) return;
      const max = Number(maxAttr);
      if (!Number.isFinite(max)) return;
      const current = Number(input.value) || 0;

      const qs = input.closest("quantity-selector");
      if (!qs) return;

      // Find the actual "+" link by reading its `quantity=` value.
      // Some templates include extra /cart/change links (eg. remove), so we can't rely on DOM order.
      const links = Array.from(qs.querySelectorAll('a.quantity-selector__button[href*="/cart/change"]'));
      if (links.length === 0) return;

      const step = Number(input.getAttribute("step") || 1) || 1;
      const expectedPlusQty = current + step;

      const parseQtyFromHref = (href) => {
        try {
          const url = new URL(href, window.location.origin);
          return Number(url.searchParams.get("quantity"));
        } catch {
          return NaN;
        }
      };

      const plusLink = links.find((a) => parseQtyFromHref(a.href) === expectedPlusQty);
      if (!plusLink) return;

      if (current >= max) {
        plusLink.setAttribute("aria-disabled", "true");
        plusLink.style.pointerEvents = "none";
        plusLink.style.opacity = "0.5";
      } else {
        plusLink.removeAttribute("aria-disabled");
        plusLink.style.pointerEvents = "";
        plusLink.style.opacity = "";
      }
    } catch {
    }
  }

  function forceEnablePdpQuantityControls() {
    const input = getPdpQtyInput();
    if (!input) return;
    try {
      input.disabled = false;
      input.readOnly = false;
      input.removeAttribute("aria-disabled");
      const qs = input.closest("quantity-selector");
      qs?.querySelectorAll("button[disabled]").forEach((b) => b.removeAttribute("disabled"));
    } catch {
    }
  }

  // Apply an API result to the PDP (qty max, ATC button, body classes).
  function applyApiToPdp(input, variantId, api) {
    state.lastApi = api;
    setBodyInventoryClass(api, variantId);

    if (!api?.status) {
      state.lastAllowedQty = null;
      state.lastAllowedVariantId = String(variantId);
      setInputMax(input, null);
      // Normal product: only undo changes this script made, otherwise leave the theme alone
      setAtcStateForVariant(variantId, false);
      return;
    }

    // New API returns remaining group stock after considering the full cart across shared group.
    const remaining = Number(api?.data?.group_total_quantity);
    if (!Number.isFinite(remaining)) return;

    const allowed = Math.max(0, remaining);
    state.lastAllowedQty = allowed;
    state.lastAllowedVariantId = String(variantId);
    setInputMax(input, allowed);
    ensureValueNotAboveMax(input);
    setAtcStateForVariant(variantId, allowed <= 0 || isNativeSoldOut(variantId));
  }

  // Any cached result for this variant + the last known cart, regardless of age.
  // Used to update the UI instantly while a fresh check runs in the background.
  function getCachedResult(productId, variantId) {
    if (!state.cartSnapshot) return null;
    const sig = cartSignature(buildCartArray(state.cartSnapshot));
    return state.cache.get(`${productId}:${variantId}:${sig}`) || null;
  }

  async function applyPdpRestriction({ productId, variantId }) {
    const input = getPdpQtyInput();
    if (!input) return;

    forceEnablePdpQuantityControls();

    // Instant path: show the cached state immediately (no network wait).
    const cachedResult = getCachedResult(productId, variantId);
    if (cachedResult) applyApiToPdp(input, variantId, cachedResult);

    const cart = await fetchCart();
    const cartArray = buildCartArray(cart);
    purgePersistentCacheForCart(cartArray);
    const api = await fetchSharedInventory({ productId, variantId, cartArray });

    // Ignore stale responses if the shopper switched variants while this request was in flight
    if (state.currentVariantId && String(variantId) !== state.currentVariantId) return;

    applyApiToPdp(input, variantId, api);
  }

  // Warm the cache for every variant of the product, so switching swatches is instant.
  async function prefetchVariants(productId) {
    try {
      if (state.prefetchedFor === String(productId)) return;
      state.prefetchedFor = String(productId);

      const variants = window.ShopifyAnalytics?.meta?.product?.variants || [];
      const ids = variants
        .map((v) => String(v.id))
        .filter((id) => id !== state.currentVariantId)
        .slice(0, PREFETCH_MAX_VARIANTS);
      if (!ids.length) return;

      const cart = await fetchCart();
      const cartArray = buildCartArray(cart);
      let i = 0;
      const worker = async () => {
        while (i < ids.length) {
          const variantId = ids[i++];
          // eslint-disable-next-line no-await-in-loop
          await fetchSharedInventory({ productId, variantId, cartArray });
        }
      };
      await Promise.all(Array.from({ length: PREFETCH_CONCURRENCY }, worker));
    } catch {
    }
  }

  async function applyCartRestrictionForInput(input) {
    const productId = input.getAttribute("data-product-id");
    const variantId = input.getAttribute("data-variant-id");
    if (!productId || !variantId) return;

    const cart = await fetchCart();
    const cartArray = buildCartArray(cart);
    purgePersistentCacheForCart(cartArray);
    const api = await fetchSharedInventory({ productId, variantId, cartArray });

    if (!api?.status) {
      setInputMax(input, null);
      return;
    }

    // New API: remaining after considering full cart.
    // For cart line max, allow keeping current qty + remaining (so user can adjust up to cap).
    const remaining = Number(api?.data?.group_total_quantity);
    if (!Number.isFinite(remaining)) return;

    const currentLineQty = Number(input.value) || 0;
    setInputMax(input, Math.max(0, remaining + currentLineQty));
    // Avoid feedback loops in cart by not emitting change here
    ensureValueNotAboveMax(input, { emitChange: false });
    updateCartUiForInput(input);
  }

  async function applyCartRestrictions() {
    if (state.cartApplyInProgress) {
      state.cartApplyQueued = true;
      return;
    }
    state.cartApplyInProgress = true;
    state.cartApplyQueued = false;
    try {
      const inputs = getCartQtyInputs();
      // Sequential to avoid bursty API storms on large carts
      for (const input of inputs) {
        // eslint-disable-next-line no-await-in-loop
        await applyCartRestrictionForInput(input);
      }
    } finally {
      state.cartApplyInProgress = false;
      if (state.cartApplyQueued) {
        state.cartApplyQueued = false;
        setTimeout(() => applyCartRestrictions(), 200);
      }
    }
  }

  function bindPdp() {
    // Variant change -> recheck
    document.addEventListener(
      "variant:change",
      (event) => {
        const variant = event?.detail?.variant;
        const variantId = variant?.id;

        // Native Shopify availability is known instantly from the event: update buttons now.
        if (variantId) {
          state.nativeSoldOut.set(String(variantId), variant.available === false);
          const productIdNow = getPdpQtyInput()?.getAttribute("data-product-id");
          const cached = productIdNow ? getCachedResult(productIdNow, variantId) : null;
          if (cached) {
            setBodyInventoryClass(cached, variantId);
          } else if (document.body?.classList.contains(BODY_CLASS_ACTIVE)) {
            // Shared product, no cache yet: apply Shopify's sold-out state now, API confirms after
            document.body.classList.toggle(BODY_CLASS_SOLDOUT, isNativeSoldOut(variantId));
          }
        } else if (document.body?.classList.contains(BODY_CLASS_ACTIVE)) {
          // Unavailable option combination on a shared product
          document.body.classList.add(BODY_CLASS_SOLDOUT);
        }

        const input = getPdpQtyInput();
        const productId = input?.getAttribute("data-product-id");
        if (!variantId || !productId) return;
        state.currentVariantId = String(variantId);
        state.currentProductId = String(productId);
        applyPdpRestriction({ productId, variantId });
      },
      true
    );

    // Cart updates are async; do multiple rechecks so PDP unlocks after cart decrease.
    const scheduleCartRecheck = () => {
      state.cartRecheckTimers.forEach((t) => window.clearTimeout(t));
      state.cartRecheckTimers = [];
      if (!state.currentProductId || !state.currentVariantId) return;

      const delays = [250, 800, 1500];
      state.cartRecheckTimers = delays.map((ms) =>
        window.setTimeout(() => {
          applyPdpRestriction({ productId: state.currentProductId, variantId: state.currentVariantId });
        }, ms)
      );
    };

    document.addEventListener("cart:change", scheduleCartRecheck, true);
    document.addEventListener("cart:refresh", scheduleCartRecheck, true);
    document.addEventListener("cart-drawer:refreshed", scheduleCartRecheck, true);

    // Add-to-cart submit: block synchronously using the last known result.
    // (preventDefault after an await is too late; the form has already submitted.)
    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        if (!form?.matches?.('form[action*="/cart/add"]')) return;

        const idInput = form.querySelector('input[name="id"]');
        const qtyInput = form.querySelector('input[name="quantity"][data-variant-id][data-product-id]');
        if (!idInput || !qtyInput) return;

        const variantId = String(idInput.value);
        const productId = qtyInput.getAttribute("data-product-id");
        if (!productId || !variantId) return;

        if (state.lastAllowedVariantId === variantId && state.lastAllowedQty !== null) {
          const allowed = state.lastAllowedQty;
          if (allowed <= 0 || Number(qtyInput.value) > allowed) {
            event.preventDefault();
            event.stopPropagation();
            setInputMax(qtyInput, allowed);
            ensureValueNotAboveMax(qtyInput);
            setAtcStateForVariant(variantId, allowed <= 0);
          }
        }
      },
      true
    );

    // Initial run from current input
    try {
      const input = getPdpQtyInput();
      const productId = input?.getAttribute("data-product-id");
      const variantId = input?.getAttribute("data-variant-id");
      if (productId && variantId) {
        captureNativeStateOnLoad(variantId);
        state.currentVariantId = String(variantId);
        state.currentProductId = String(productId);
        applyPdpRestriction({ productId, variantId }).then(() => {
          // Only warm the cache for shared-variant products; normal products make no extra calls
          if (state.lastApi?.status) prefetchVariants(productId);
        });
      }
    } catch {
    }
  }

  function bindCart() {
    // Input change
    document.addEventListener(
      "change",
      (event) => {
        const input = event.target?.closest?.('input[data-line-key][data-variant-id][data-product-id]');
        if (!input) return;
        applyCartRestrictionForInput(input);
      },
      true
    );

    // Block +/- link clicks
    document.addEventListener(
      "click",
      async (event) => {
        const link = event.target?.closest?.('a[href*="/cart/change"]');
        if (!link) return;

        const url = new URL(link.href, window.location.origin);
        const lineKey = url.searchParams.get("id");
        const targetQty = Number(url.searchParams.get("quantity"));
        if (!lineKey || !Number.isFinite(targetQty)) return;

        const input = getCartQtyInputByLineKey(lineKey);
        if (!input) return;

        await applyCartRestrictionForInput(input);
        const maxAttr = input.getAttribute("max");
        if (!maxAttr) return;
        const max = Number(maxAttr);
        if (!Number.isFinite(max)) return;

        if (targetQty > max) {
          event.preventDefault();
          event.stopPropagation();
          input.value = String(max);
          input.dispatchEvent(new Event("change", { bubbles: true }));
          updateCartUiForInput(input);
        }
      },
      true
    );

    // Keep cart restrictions updated when cart re-renders
    let cartDebounceTimer = null;
    const onCartChanged = () => {
      if (cartDebounceTimer) window.clearTimeout(cartDebounceTimer);
      cartDebounceTimer = window.setTimeout(() => applyCartRestrictions(), 300);
    };
    document.addEventListener("cart:change", onCartChanged, true);
    document.addEventListener("cart:refresh", onCartChanged, true);
    document.addEventListener("cart-drawer:refreshed", onCartChanged, true);

    // Checkout guard
    document.addEventListener(
      "submit",
      async (event) => {
        const form = event.target;
        if (!form?.matches?.("form.cart-page")) return;
        const active = document.activeElement;
        const isCheckout = active?.getAttribute?.("name") === "checkout";
        if (!isCheckout) return;

        await applyCartRestrictions();
        const invalid = getCartQtyInputs().some((input) => {
          const maxAttr = input.getAttribute("max");
          if (!maxAttr) return false;
          const max = Number(maxAttr);
          return Number(input.value) > max;
        });

        if (invalid) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    applyCartRestrictions();
  }

  let initialized = false;
  function init() {
    // DOMContentLoaded, xDOMContentLoaded and xsts-loaded can all fire: bind only once
    if (initialized) return;
    initialized = true;
    const isProduct = document.documentElement?.classList?.toString?.().includes("template-product") || window.currentTemplate === "product";
    const isCartPage = !!document.querySelector("form.cart-page") || document.documentElement?.classList?.toString?.().includes("template-cart") || window.currentTemplate === "cart";
    const hasCartDrawer = !!document.querySelector("cart-drawer") || !!document.querySelector("[data-drawer-id='cart-drawer']");

    if (isProduct) bindPdp();
    if (isCartPage || hasCartDrawer) bindCart();
  }

  // Normal + optimized lifecycle hooks
  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("xDOMContentLoaded", init);
  window.addEventListener("xsts-loaded", init);
})();